/**
 * Single source of truth for all magic numbers. Subsystem agents read from here;
 * shader struct sizes and pipeline layouts must match these values.
 */

export const config = {
  // === Generation ===
  heightmapSize: 1024,             // r32float texture, square
  terrainGridResolution: 513,       // mesh verts per side (513 = 512 quads)

  // World scale: terrain spans WORLD_SIZE × WORLD_SIZE in xz, y in [-WORLD_HEIGHT, WORLD_HEIGHT/2]
  worldSize: 4096,
  worldHeight: 640,

  // === Erosion ===
  erosionIterations: 150,

  // === Roads ===
  citySeedCount: 8,
  jfaPasses: 10,                    // log2(1024)
  // Road radius in heightmap-texel units (1024² grid). Major+minor roads fade past this.
  roadRadius: 220,
  // Minor-road grid spacing in heightmap-texel units, and per-line stroke half-width.
  roadGridSpacing: 24,
  roadLineWidth: 1.5,

  // === Buildings ===
  maxBuildingInstances: 32_768,
  // Candidate grid stride in heightmap texels. Adjacent candidates are
  // `buildingCellStride * (worldSize / heightmapSize)` world units apart. Keep
  // this large enough that two adjacent max-footprint candidates can't overlap.
  buildingCellStride: 4,
  buildingDensityScale: 1.8,
  // Reject building candidates whose 3×3 footprint sample spans more than this in heightmap units
  // (0..1 range). 0.025 ≈ 8 world-units of vertical relief across the lot at worldHeight=320.
  buildingFootprintRangeMax: 0.025,
  // Footprint range in world units (full width). The categorical type roll in
  // gen/buildings.wgsl picks per-category sub-ranges; these are absolute clamps.
  // Max must satisfy `buildingFootprintMax < buildingCellStride * (worldSize / heightmapSize)`
  // so two adjacent max-footprint lots can't overlap.
  buildingFootprintMin: 5,
  buildingFootprintMax: 12,
  // Heights in world units. The cubic-cluster-taper + aspect clamp keep towers from
  // becoming 16:1 pencils.
  buildingHeightBase: 6,
  buildingHeightMax: 110,
  buildingMaxAspect: 4.5,           // height ≤ min(footprintX, footprintZ) × this
  villageDensityScale: 0.15,        // extra multiplier when biome ∈ {lowland, forest}
  // Flatten pass: pad (in heightmap texels) added around each footprint at full target height,
  // and feather distance (texels) over which the platform smoothly blends back to original.
  buildingPlatformPadTexels: 1.5,
  buildingPlatformFeatherTexels: 3.0,

  // === Trees ===
  maxTreeInstances: 65_536,

  // === Cars ===
  carInstanceCount: 1000,
  // Half-extents in world units; full length × width × height = 6.0 × 2.4 × 1.6.
  carHalfLen: 3.0,
  carHalfWid: 1.2,
  carHalfHei: 0.8,
  // Speed range in world-units / second. With block spacing ≈ 96 wu, ~25 wu/s ≈ 4s per block.
  carSpeedMin: 22,
  carSpeedMax: 34,
  // Lookahead distance for road sampling and steering probes (world units).
  carLookaheadWu: 7.0,
  // Mask threshold below which a sample counts as off-road.
  carRoadThreshold: 0.25,
  // Frames before a stuck/off-road car respawns at a random drivable texel.
  carRespawnAfterOffRoadFrames: 30,
  // Frames between forced steering re-evaluations (intersection picks). Jittered per car.
  carReevaluatePeriodFrames: 24,

  // === Camera ===
  fovYDeg: 50,
  near: 0.5,
  far: 12000,
  // Cinematic loop. Synced to the day/night cycle so the same camera vantage doesn't
  // recur at opposite lighting; the eye loop completes once per day.
  cameraLoopSeconds: 360,
  // Terrain-clearance for the eye and look-target. Eye clearance accounts for the
  // tallest possible building (~88) plus headroom.
  cameraEyeClearance: 200,
  cameraTargetClearance: 30,
  // Baseline downward tilt (radians) folded into the cinematic look direction so
  // the camera frames the city more top-down. Re-aims the look ray only — eye
  // height is unchanged. Adds on top of mouse look. ~0.30 rad ≈ 17°.
  cameraDownwardPitch: 0.30,
  cameraWaypointCount: 6,
  cameraTargetCount: 6,             // share period with waypoints to avoid loop-seam drift

  // === Mouse look / zoom ===
  mouseSens: 0.0035,                // radians per pixel of drag
  mousePitchLimit: 1.45,            // hard clamp on accumulated pitch (rad), just under π/2
  mouseReleaseDecay: 2.5,           // 1/s exponential decay of yaw/pitch when LMB released
  zoomStep: 0.0015,                 // exponent applied to normalized wheel lines
  zoomFovMin: 0.2,                  // narrowest FOV multiplier
  zoomFovMax: 2.2,                  // widest FOV multiplier

  // === Free-fly camera ===
  flyMoveSpeed: 250,                // wu/s base movement speed
  flyFastMultiplier: 4.0,           // shift-held speed scale
  flyPitchLimit: 1.553,             // ~89° — tighter than mousePitchLimit; prevents gimbal flip
  flyEyeClearance: 5,               // wu above terrain (vs cinematic's 200 — fly lets you skim low)

  // === Day/night ===
  dayNightCycleSeconds: 360,        // 6 minutes
  axialTiltDeg: 23.5,
  lunarCycleDays: 8,                // full moon → new moon → full = 8 day/night loops

  // === Shadows ===
  shadowMapSize: 2048,

  // === Sky ===
  // (Preetham-lite parameters live inside the shader)

  // === Render ===
  msaaSamples: 1,                   // post-pass adds AA / can use TAA later

  // === Seeding ===
  // Resolved at startup from location.hash, with this fallback.
  fallbackSeed: 'manhattan-2026',
} as const;

export type Config = typeof config;
